// @ts-nocheck
/**
 * Ludipek B2B cart snapshot.
 *
 * Observes cart mutations, calls POST {apiBaseUrl}/cart/sign with the current cart items, and
 * writes the signed payload to Shopify cart attributes (b2b_payload, b2b_sig, b2b_sig_alg) via
 * POST /cart/update.js. The Shopify discount function reads those attributes at checkout to
 * enforce B2B prices.
 *
 * Checkout guard:
 *   - While a sign request is in flight, the cart form submit is blocked (capture-phase).
 *   - Logged-out customers are blocked outright with an inline banner — they would otherwise
 *     pay the Shopify default (max × 1.5 safety buffer) because the discount function has no
 *     payload to enforce.
 *   - Sign failures block the submit and render an inline error banner (never window.alert).
 *   - A cart signed in one currency and presented in another is blocked with the Peru notice and
 *     the Peru sales contact: the discount function refuses such a cart and the buyer would pay the
 *     ceiling (requireSignedCurrencyMatches).
 *
 * NetSuite IDs for cart items are scraped from `data-netsuite-id` attributes on cart row <tr>
 * elements in the DOM, because Shopify's /cart.js response does NOT include product metafields.
 */
(function () {
  const BOOTSTRAP = window.__LUDIPEK_B2B__ || null;
  const DEBOUNCE_MS = 300;
  const FETCH_TIMEOUT_MS = 10_000;
  const BANNER_ID = 'b2b-error-banner';
  const DEFAULT_BUTTON_LABEL_KEY = 'data-b2b-original-label';

  // Capture the original fetch BEFORE any monkeypatching so that writeCartAttributes and our own
  // cart read can bypass the patch and avoid triggering a self-refresh loop.
  const originalFetch = window.fetch.bind(window);

  let inflightSignPromise = null;
  let lastSignError = null;
  // Cart mutations seen vs. the newest one a finished sign covers. The checkout guard compares them:
  // a signature older than the cart is stale even when no sign is in flight yet — the gap between a
  // /cart/change and the end of its debounce is exactly how order #1015 reached checkout with a
  // quantity-5 signature on a quantity-4 line, so the discount Function skipped it and the buyer paid
  // the ceiling.
  let mutationGeneration = 0;
  let signedGeneration = 0;
  const pendingMutations = new Set();
  // What this tab's newest sign accounted for: line key → quantity for every line it signed, plus lines
  // whose row carries no NetSuite id (never signable; they pay the ceiling by design). A line whose row
  // was missing from the page is left out. signedCartSignature is the b2b_sig it wrote, so the checkout
  // guard can tell when another tab has signed the cart since (signatureCovers). Both null until a sign.
  let signedCartQuantities = null;
  let signedCartSignature = null;
  // The currency inside the payload behind signedCartSignature (null when nothing was signed, or the payload
  // could not be read). The checkout guard compares it with the cart's presentment currency.
  let signedCartCurrency = null;

  function isSignatureStale() {
    return signedGeneration < mutationGeneration;
  }

  function noteMutation(request) {
    mutationGeneration += 1;
    if (request && typeof request.finally === 'function') {
      pendingMutations.add(request);
      request.finally(() => pendingMutations.delete(request)).catch(() => {});
    }
  }

  // Let every cart mutation already sent land, but wait no longer than a fetch may take: Horizon's own
  // /cart/change has no timeout, and a stalled one must not hold the checkout forever. Throws on timeout,
  // because signing before that mutation lands could sign the wrong quantities.
  async function settlePendingMutations() {
    if (pendingMutations.size === 0) return;
    let timeoutId = null;
    const timedOut = new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve(true), FETCH_TIMEOUT_MS);
    });
    const settled = Promise.allSettled([...pendingMutations]).then(() => false);
    const didTimeOut = await Promise.race([settled, timedOut]);
    clearTimeout(timeoutId);
    if (didTimeOut) throw new Error('a cart update did not finish in time');
  }

  // Before a checkout goes out: let every cart mutation already sent land, then sign the cart as it is
  // now if the stored signature is older than it (or the last sign failed).
  async function ensureFreshSignature() {
    await settlePendingMutations();
    if (inflightSignPromise !== null) {
      try {
        await inflightSignPromise;
      } catch {
        // lastSignError carries it; the retry below decides.
      }
    }
    if (lastSignError !== null || isSignatureStale()) await scheduleRefresh();
  }

  // --- Price skeleton (loading state) ----------------------------------------------------------
  // While a quantity edit is being re-signed and the cart re-rendered (~1s), the cart briefly shows
  // the 1.5x ceiling price. We overlay a shimmer skeleton on the cart price elements during that
  // window so the buyer never sees the wrong price flash. Scoped to cart-only classes, so toggling
  // the class on <html> can never affect PDP/collection prices (which reuse the same data-b2b-price
  // hook).
  const LOADING_CLASS = 'b2b-price-loading';

  function injectSkeletonStyles() {
    if (document.getElementById('b2b-price-skeleton-styles')) return;
    const style = document.createElement('style');
    style.id = 'b2b-price-skeleton-styles';
    // Target only price VALUE elements (not labels or the checkout button): the line unit price,
    // the line total cell, the summary subtotal value, the tax breakdown's Neto and tax values
    // (cart-summary.liquid, markets with a tax rate), and the summary total value.
    const targets = [
      '.cart-items__unit-price-wrapper',
      '.cart-items__price',
      '.cart-totals__original-value',
      '[data-b2b-cart-net]',
      '[data-b2b-cart-tax]',
      '[data-b2b-cart-total]',
    ];
    const sel = (suffix) => targets.map((t) => '.' + LOADING_CLASS + ' ' + t + suffix).join(',\n');
    // Use visibility (not color: transparent): the prices are <text-component> web components and
    // color doesn't pierce shadow DOM, so the number stayed visible. visibility:hidden on the host
    // hides the whole component (incl. shadow content); the ::after re-shows itself as the shimmer.
    style.textContent = [
      sel('') + ' { position: relative !important; visibility: hidden !important; }',
      sel('::after') + ' {',
      '  visibility: visible; content: ""; position: absolute; inset: 0; border-radius: 6px;',
      '  pointer-events: none; z-index: 1;',
      '  background: linear-gradient(100deg, rgba(0,0,0,0.06) 30%, rgba(0,0,0,0.13) 50%, rgba(0,0,0,0.06) 70%);',
      '  background-size: 200% 100%; animation: b2b-price-skeleton 1.1s ease-in-out infinite;',
      '}',
      '@keyframes b2b-price-skeleton { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }',
    ].join('\n');
    document.head.appendChild(style);
  }

  function setPriceLoading(on) {
    document.documentElement.classList.toggle(LOADING_CLASS, on);
  }

  function logDebug(message, details) {
    if (details === undefined) {
      console.debug('[b2b-cart-snapshot]', message);
    } else {
      console.debug('[b2b-cart-snapshot]', message, details);
    }
  }

  function logWarn(message, details) {
    if (details === undefined) {
      console.warn('[b2b-cart-snapshot]', message);
    } else {
      console.warn('[b2b-cart-snapshot]', message, details);
    }
  }

  function logError(message, details) {
    if (details === undefined) {
      console.error('[b2b-cart-snapshot]', message);
    } else {
      console.error('[b2b-cart-snapshot]', message, details);
    }
  }

  function getEffectiveCountry() {
    if (window.LudipekB2BLocation && window.LudipekB2BLocation.getEffectiveCountry) {
      return window.LudipekB2BLocation.getEffectiveCountry();
    }
    return BOOTSTRAP ? BOOTSTRAP.storeCountry : null;
  }

  function bootstrapIsUsable() {
    if (BOOTSTRAP === null) return false;
    if (!BOOTSTRAP.apiBaseUrl) return false;
    if (!getEffectiveCountry()) return false;
    if (currentCustomerId() == null) {
      logDebug('no customer_id for selected location — logged-out or pending cart, skipping cart signing');
      return false;
    }
    return true;
  }

  // The customer_id sent to /cart/sign is whichever NS customer drives pricing at the buyer's
  // currently selected CompanyLocation. Switching locations swaps the NS id.
  function currentCustomerId() {
    return window.LudipekB2BLocation ? window.LudipekB2BLocation.getCurrentNetsuiteCustomerId() : null;
  }

  async function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      return await originalFetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async function fetchCart() {
    const response = await fetchWithTimeout('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) {
      throw new Error(`GET /cart.js failed with HTTP ${response.status}`);
    }
    return response.json();
  }

  /**
   * Extract sign items by joining Shopify /cart.js (source of truth for quantity + order) with
   * `data-netsuite-id` attributes scraped from cart-row <tr> elements in the DOM.
   *
   * Shopify's /cart.js response does NOT include product metafields, so we cannot read the
   * NetSuite id from the cart JSON directly. Rows are matched by `data-key` (Shopify line key).
   */
  function getSelectedRegion() {
    return window.LudipekB2BLocation ? window.LudipekB2BLocation.getStoreCode() : null;
  }

  // Product titles by NetSuite id for the cart being signed, so a rejection can name the product.
  const productTitleById = new Map();

  // /cart/sign rejects a quantity that is not a whole sales multiple with a 409 whose detail names the
  // product by its NetSuite id ("El producto 1968 se vende de a 6. Ajusta la cantidad ..."). Give the buyer
  // that reason, with the product's title, instead of the generic "could not verify your prices"; every
  // other failure keeps the generic message.
  function buyerMessageFor(status, text) {
    if (status !== 409) return null;
    let detail = null;
    try {
      detail = JSON.parse(text).detail;
    } catch {
      return null;
    }
    // Fecha corta lines above the branch's Fecha corta stock: a structured detail (code mirrors the API's
    // FECHA_CORTA_STOCK_ERROR_CODE) whose message opens "El producto {id} …" too.
    if (detail && detail.code === 'fecha_corta_stock_exceeded' && typeof detail.message === 'string') {
      const productTitle = productTitleById.get(Number(detail.product_id));
      return productTitle
        ? detail.message.replace(`El producto ${detail.product_id}`, `«${productTitle}»`)
        : detail.message;
    }
    if (typeof detail !== 'string') return null;
    const match = detail.match(/^El producto (\d+) se vende de a/);
    if (match === null) return null;
    const title = productTitleById.get(Number(match[1]));
    return title ? detail.replace(`El producto ${match[1]}`, `«${title}»`) : detail;
  }

  // Returns the sign items plus accountedQuantities (line key → quantity) for every line the sign can
  // speak for: the ones it signs, and the ones whose row is on the page without a NetSuite id. A line
  // with no row on the page is in neither, so the checkout guard re-signs rather than trust it.
  function extractSignItems(cart) {
    productTitleById.clear();
    const rowsByKey = new Map();
    document.querySelectorAll('tr[data-key]').forEach((row) => {
      const key = row.getAttribute('data-key');
      if (key) rowsByKey.set(key, row.getAttribute('data-netsuite-id') || '');
    });

    const region = getSelectedRegion();
    const items = [];
    const accountedQuantities = new Map();
    for (const item of cart.items || []) {
      const netsuiteIdRaw =
        rowsByKey.get(item.key) ||
        item.properties?._netsuite_id ||
        null;
      if (netsuiteIdRaw == null || netsuiteIdRaw === '') {
        const rowOnPage = rowsByKey.has(item.key);
        if (rowOnPage) accountedQuantities.set(item.key, item.quantity);
        logWarn(rowOnPage ? 'cart item has no netsuite_id, skipping' : 'cart item has no row on the page yet', {
          variant: item.variant_id,
          handle: item.handle,
          key: item.key,
        });
        continue;
      }
      accountedQuantities.set(item.key, item.quantity);
      const productId = Number(netsuiteIdRaw);
      if (!Number.isFinite(productId) || productId <= 0) continue;
      // Lot status from the variant's "Lot status" option, used by the cart-sign API to apply
      // the FECHA CORTA discount factor server-side. Default 'standard' when the option is
      // absent (i.e. the product hasn't been split yet).
      const lotStatusRaw = (item.options_with_values || []).find(
        (option) => option && option.name === 'Lot status',
      );
      const lotStatus = lotStatusRaw && String(lotStatusRaw.value).toLowerCase().includes('fecha')
        ? 'fecha_corta'
        : 'standard';
      if (item.product_title) productTitleById.set(productId, String(item.product_title));
      items.push({
        product_id: productId,
        quantity: item.quantity,
        region: region,
        lot_status: lotStatus,
      });
    }
    return { items, accountedQuantities };
  }

  async function signCart(items) {
    const url = `${BOOTSTRAP.apiBaseUrl.replace(/\/$/, '')}/cart/sign`;
    const body = {
      store_country: getEffectiveCountry(),
      customer_id: currentCustomerId(),
      items,
    };
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': BOOTSTRAP.apiShopifyKey,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const text = await response.text();
      const error = new Error(`POST /cart/sign failed with HTTP ${response.status}: ${text}`);
      error.buyerMessage = buyerMessageFor(response.status, text);
      throw error;
    }
    return response.json();
  }

  // The currency the API signed in, read from the payload of the /cart/sign answer: base64url JSON without
  // padding, the same bytes the discount function decodes. null when it cannot be read.
  function signedPayloadCurrency(payloadBase64) {
    try {
      const base64 = String(payloadBase64).replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded));
      return typeof payload.currency === 'string' && payload.currency !== '' ? payload.currency : null;
    } catch {
      return null;
    }
  }

  // Tells b2b-pricing.js the cart's signature changed ('' when it was cleared): the cart summary is painted from the
  // signed prices, so it repaints from this one.
  function announceSignature(payloadBase64) {
    document.dispatchEvent(new CustomEvent('b2b:cart-signed', { detail: { payload: payloadBase64 || '' } }));
  }

  async function writeCartAttributes(payload) {
    const response = await fetchWithTimeout('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          b2b_payload: payload.b2b_payload,
          b2b_sig: payload.b2b_sig,
          b2b_sig_alg: payload.b2b_sig_alg,
        },
      }),
    });
    if (!response.ok) {
      throw new Error(`POST /cart/update.js failed with HTTP ${response.status}`);
    }
  }

  async function refreshSignature() {
    const cart = await fetchCart();
    const cartItemCount = (cart.items || []).length;

    // Only clear the signature when the cart is genuinely EMPTY. Clearing it because the DOM scrape
    // came up empty is destructive: the discount function enforces a per-line quantity match, so a
    // missing/stale payload means the buyer pays the 1.5x ceiling — and nothing re-triggers a sign,
    // so it persists to checkout. This was the cause of "edit quantity in cart → reverts to Shopify
    // price".
    if (cartItemCount === 0) {
      logDebug('cart is empty, clearing attributes');
      await writeCartAttributes({ b2b_payload: '', b2b_sig: '', b2b_sig_alg: '' });
      signedCartQuantities = new Map();
      signedCartSignature = '';
      signedCartCurrency = null;
      announceSignature('');
      return;
    }

    // The cart rows are re-rendered after a quantity change, so a row can briefly be missing from the
    // page. Retry for up to ~1s while any line has no row, rather than signing a partial cart.
    let extracted = extractSignItems(cart);
    for (
      let attempt = 0;
      attempt < 6 && extracted.accountedQuantities.size < cartItemCount;
      attempt += 1
    ) {
      await new Promise((resolve) => setTimeout(resolve, 150));
      extracted = extractSignItems(cart);
    }
    const { items, accountedQuantities } = extracted;
    if (items.length === 0) {
      if (accountedQuantities.size === cartItemCount) {
        // Every row is on the page and none carries a NetSuite id: there is nothing to sign, and those
        // lines pay the ceiling whatever the attributes say.
        logWarn('no line in the cart carries a netsuite_id — nothing to sign');
        await writeCartAttributes({ b2b_payload: '', b2b_sig: '', b2b_sig_alg: '' });
        signedCartQuantities = accountedQuantities;
        signedCartSignature = '';
        signedCartCurrency = null;
        announceSignature('');
        return;
      }
      // Cart has items but no rows to resolve them from (DOM not ready). Keep the existing signature
      // rather than wiping it — never strand the buyer on the ceiling price.
      logError('cart has items but no netsuite_ids resolved — keeping the existing signature', {
        cart_item_count: cartItemCount,
      });
      return;
    }

    const signed = await signCart(items);
    await writeCartAttributes(signed);
    signedCartQuantities = accountedQuantities;
    signedCartSignature = signed.b2b_sig;
    signedCartCurrency = signedPayloadCurrency(signed.b2b_payload);
    announceSignature(signed.b2b_payload);
    if (accountedQuantities.size < cartItemCount) {
      logWarn(`signed ${items.length} items; ${cartItemCount - accountedQuantities.size} lines had no row on the page`);
    }
    // The server just priced these products, so their display prices may have moved: demote them to
    // stale in the scope they were signed in. The next render still paints them instantly and
    // refetches them behind the buyer.
    //
    // This used to be invalidateAll(), from the first cut of this file, "so the next page load
    // re-fetches prices aligned with the quantities used for signing". Display prices are quoted at
    // qty=1 and never depend on cart quantities, though, and this runs after EVERY sign: every cart
    // edit, and every page load with a non-empty cart whose rows are on the page (the cart page, and
    // every page while the cart drawer is on). Wiping every entry made each cart re-render a cold
    // /products/prices/fetch behind a skeleton and defeated the stale-while-revalidate cache. The
    // display cache never decides money; the signature just written does.
    if (window.LudipekB2BCache && typeof window.LudipekB2BCache.markStale === 'function') {
      window.LudipekB2BCache.markStale(
        currentCustomerId(),
        getEffectiveCountry(),
        getSelectedRegion(),
        items.map((item) => item.product_id),
      );
    }
    logDebug(`signed ${items.length} items`);

    // Only re-render (and only then) when we're in a loading cycle — i.e. a cart mutation, not the
    // initial page-load sign (the page already rendered with the correct discount). Nudge Horizon to
    // re-render so the line price reflects the just-signed discount: the cart was rendered the instant
    // the quantity changed — BEFORE this re-sign finished — so it shows the 1.5x ceiling until
    // something re-renders it. 'discount:update' (ThemeEvents.discountUpdate) makes <cart-items>
    // re-render via the Section Rendering API (a GET, not a cart mutation), so it does NOT re-trigger
    // signing (our own listener is 'cart:updated', a different event). The brief wait keeps the
    // skeleton up until the re-rendered B2B price is in, so the ceiling never shows through.
    if (document.documentElement.classList.contains(LOADING_CLASS)) {
      document.dispatchEvent(new CustomEvent('discount:update', { detail: { data: {} }, bubbles: true }));
      await new Promise((resolve) => setTimeout(resolve, 400));
    }
  }

  function scheduleRefresh() {
    if (!bootstrapIsUsable()) {
      // No sign will run, so nothing else would ever clear a skeleton a mutation already raised.
      setPriceLoading(false);
      return null;
    }
    const generation = mutationGeneration;
    const promise = (async () => {
      try {
        await refreshSignature();
        lastSignError = null;
        signedGeneration = Math.max(signedGeneration, generation);
      } catch (error) {
        lastSignError = error;
        logError('sign refresh failed', { error: String(error) });
        throw error;
      } finally {
        if (inflightSignPromise === promise) {
          inflightSignPromise = null;
        }
        // Clear the price skeleton once this re-sign cycle (and its re-render wait) is done.
        setPriceLoading(false);
      }
    })();
    inflightSignPromise = promise;
    return promise;
  }

  function debounce(fn, delay) {
    let timeoutId = null;
    return function debounced(...args) {
      if (timeoutId !== null) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        fn.apply(this, args);
      }, delay);
    };
  }

  const debouncedRefresh = debounce(() => scheduleRefresh(), DEBOUNCE_MS);

  // A cart mutation will re-sign + re-price; show the skeleton immediately (before the debounce) so
  // the buyer never sees the ceiling price flash. Only when we'll actually sign (logged-in B2B) —
  // scheduleRefresh's finally clears it. Skipped for logged-out/pending carts (no sign happens).
  function triggerRefresh() {
    noteMutation(null);
    if (bootstrapIsUsable()) setPriceLoading(true);
    debouncedRefresh();
  }

  // Cart mutation detection — Shopify themes commonly dispatch 'cart:updated' after /cart/add,
  // /cart/change, /cart/update. Listen for that plus explicit AJAX hooks below.
  document.addEventListener('cart:updated', () => triggerRefresh());

  // Intercept fetch() calls to Shopify cart mutation endpoints as a backstop for themes that
  // don't dispatch cart:updated. We explicitly NOT match /cart/update.js — our own writes go
  // through fetchWithTimeout which uses originalFetch (bypassing this patch), so the match
  // below never triggers on our own attribute writes. We also exclude /cart/update here as a
  // belt-and-braces measure against third-party code that might use window.fetch directly.
  window.fetch = function patchedFetch(input, init) {
    const result = originalFetch(input, init);
    const url = typeof input === 'string' ? input : input && input.url;
    if (typeof url === 'string' && (url.includes('/cart/add') || url.includes('/cart/change'))) {
      // Mark the signature stale synchronously, before the response or the debounce: a checkout
      // clicked in that window must wait for this mutation and re-sign (ensureFreshSignature).
      noteMutation(result);
      // Skeleton synchronously on the mutation so it covers Horizon's immediate re-render too.
      if (bootstrapIsUsable()) setPriceLoading(true);
      // Re-sign even when the request itself failed: the re-sign's finally is what lowers the
      // skeleton (it used to stay up for good on a network error), and a mutation whose response
      // was lost may still have changed the cart, which then needs a fresh signature anyway.
      result.then(
        () => debouncedRefresh(),
        () => debouncedRefresh(),
      );
    }
    return result;
  };

  // --- Inline error banner ---------------------------------------------------------------
  // links: optional [{ href, label }] rendered after the message (the Peru notice's phone and email).
  function showBanner(form, message, links) {
    let banner = document.getElementById(BANNER_ID);
    if (banner === null) {
      banner = document.createElement('div');
      banner.id = BANNER_ID;
      banner.setAttribute('role', 'alert');
      banner.style.cssText =
        'padding:12px 16px;margin:12px 0;border:1px solid currentColor;border-radius:4px;color:#b00020;background:rgba(176,0,32,0.08);';
      form.parentNode.insertBefore(banner, form);
    }
    banner.textContent = message;
    (links || []).forEach((link, index) => {
      banner.append(index === 0 ? ' ' : ' · ');
      const anchor = document.createElement('a');
      anchor.href = link.href;
      anchor.textContent = link.label;
      anchor.style.cssText = 'color:inherit;font-weight:600;text-decoration:underline;white-space:nowrap;';
      banner.append(anchor);
    });
  }

  function hideBanner() {
    const banner = document.getElementById(BANNER_ID);
    if (banner !== null) banner.remove();
  }

  function message(key, fallback) {
    const messages = (BOOTSTRAP && BOOTSTRAP.messages) || {};
    return messages[key] || fallback;
  }

  // --- Checkout button state -------------------------------------------------------------
  function setButtonBusy(button, busy) {
    if (button === null) return;
    if (busy) {
      if (!button.hasAttribute(DEFAULT_BUTTON_LABEL_KEY)) {
        const label = button.querySelector('.button-text') || button;
        button.setAttribute(DEFAULT_BUTTON_LABEL_KEY, label.textContent || '');
        label.textContent = message('checkoutVerifying', 'Verifying prices…');
      }
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    } else {
      const original = button.getAttribute(DEFAULT_BUTTON_LABEL_KEY);
      if (original !== null) {
        const label = button.querySelector('.button-text') || button;
        label.textContent = original;
        button.removeAttribute(DEFAULT_BUTTON_LABEL_KEY);
      }
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  // --- Quantities on screen ----------------------------------------------------------------
  // Horizon shows a +/- or typed quantity in its input at once but sends the /cart/change only after a
  // 300 ms debounce, and a native submit of the cart form posts those inputs (updates[]) itself. A
  // checkout clicked inside that window changed the cart in the same request that opened checkout, and no
  // intercepted mutation ever marked the signature stale: on the preview theme (2026-09-23) a line raised
  // to 2 reached checkout signed for 1, the discount Function skipped it, and it cost the ceiling.
  //
  // Rows Horizon is animating out after a removal are skipped: their inputs keep the old quantity.
  function formQuantities(form) {
    const quantities = new Map();
    form.querySelectorAll('input[name="updates[]"]').forEach((input) => {
      if (input.closest('.removing')) return;
      const key = input.closest('[data-key]')?.getAttribute('data-key');
      const quantity = Number.parseInt(input.value, 10);
      if (key && Number.isInteger(quantity) && quantity >= 0) quantities.set(key, quantity);
    });
    return quantities;
  }

  // Bring the cart to the quantities on screen, by line key rather than by position, once Horizon's own
  // requests have landed. A line the cart no longer has is left out, never re-added.
  async function applyFormQuantities(form) {
    await settlePendingMutations();
    const cart = await fetchCart();
    const cartQuantities = new Map((cart.items || []).map((item) => [item.key, item.quantity]));
    const updates = {};
    for (const [key, quantity] of formQuantities(form)) {
      if (cartQuantities.has(key) && cartQuantities.get(key) !== quantity) updates[key] = quantity;
    }
    if (Object.keys(updates).length === 0) return;
    const request = fetchWithTimeout('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    noteMutation(request);
    const response = await request;
    if (!response.ok) {
      throw new Error(`POST /cart/update.js failed with HTTP ${response.status}`);
    }
  }

  // Whether the signature on this cart is the one this tab wrote, and accounts for every line at its
  // current quantity. The cart comes from the server, not from this tab's memory: another tab, or a page
  // restored from the back/forward cache, may have changed it.
  function signatureCovers(cart) {
    if (signedCartQuantities === null || signedCartSignature === null) return false;
    if (((cart.attributes || {}).b2b_sig || '') !== signedCartSignature) return false;
    return (cart.items || []).every((item) => signedCartQuantities.get(item.key) === item.quantity);
  }

  // The Peru sales contact as banner links, from the bootstrap (settings.ludipek_phone_pe and the sales email).
  function salesContactPeLinks() {
    const contact = (BOOTSTRAP && BOOTSTRAP.salesContactPe) || {};
    const links = [];
    if (contact.phone) {
      // Keep the leading + for international dialing; tel: only needs the spaces and punctuation gone.
      links.push({ href: `tel:${String(contact.phone).replace(/[^\d+]/g, '')}`, label: contact.phone });
    }
    if (contact.email) links.push({ href: `mailto:${contact.email}`, label: contact.email });
    return links;
  }

  // The discount function reads each line's presentment amount and refuses the whole cart when the signed
  // currency is not the presentment one, so such a checkout charges the ceiling. Peru is always charged in PEN
  // (owner decision, 2026-09-27): /cart/sign signs Peru carts in PEN, while the Peru market presents CLP until
  // PEN is enabled for it in Markets. Until then a Peru buyer's sales rep takes the order; the cart keeps
  // showing the PEN prices. Nothing here names a market, so the block lifts itself the day both currencies
  // agree. A cart with nothing signed has no discount to refuse and passes.
  function requireSignedCurrencyMatches(cart) {
    if (signedCartSignature === '') return;
    const presentmentCurrency = typeof cart.currency === 'string' && cart.currency !== '' ? cart.currency : null;
    if (signedCartCurrency === null || presentmentCurrency === null) {
      throw new Error(
        `cannot compare currencies: signed ${signedCartCurrency ?? 'unreadable'}, presented ${presentmentCurrency ?? 'unreadable'}`
      );
    }
    if (signedCartCurrency === presentmentCurrency) return;
    const error = new Error(`cart signed in ${signedCartCurrency} but presented in ${presentmentCurrency}`);
    error.buyerMessage = message(
      'checkoutPeruOffline',
      'Orders from Peru cannot be paid online for now. Your sales representative will take your order: email or call us.'
    );
    error.buyerLinks = salesContactPeLinks();
    throw error;
  }

  // Make the signature cover the cart exactly as it will reach checkout. Bounded: a buyer who keeps
  // editing while this runs gets another pass, not an endless wait.
  async function signCheckoutCart(form) {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (!bootstrapIsUsable()) throw new Error('B2B pricing is unavailable');
      await applyFormQuantities(form);
      await ensureFreshSignature();
      // Read the cart after signing: its b2b_sig must be the one this tab just wrote, in the cart's currency.
      const cart = await fetchCart();
      if (signatureCovers(cart)) {
        requireSignedCurrencyMatches(cart);
        return;
      }
      // The cart moved without passing through the fetch patch, or a line had no row on the page when
      // it was signed: mark the signature stale so the next pass signs the cart as it is.
      noteMutation(null);
    }
    throw new Error('the cart kept changing while the checkout was being signed');
  }

  // --- Checkout guard --------------------------------------------------------------------
  function installCheckoutGuard() {
    const form = document.querySelector('form#cart-form, form[action="/cart"]');
    if (!form) {
      logDebug('no cart form found — checkout guard not installed');
      return;
    }
    const draftSubmitButton = form.querySelector('button[data-b2b-draft-submit]');
    // Horizon renders the checkout button outside the form (form="cart-form"), so look it up among the
    // form's associated elements rather than its descendants, or the busy state never shows.
    const checkoutControl = form.elements.namedItem('checkout');
    const button =
      draftSubmitButton ||
      (checkoutControl instanceof HTMLButtonElement ? checkoutControl : null) ||
      form.querySelector('button#checkout');
    // One checkout at a time: a second click while the first is signing would start a parallel run.
    let checkoutInProgress = false;

    // The back button from checkout restores this page from the back/forward cache, busy button and all.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      checkoutInProgress = false;
      setButtonBusy(button, false);
    });

    form.addEventListener(
      'submit',
      async (event) => {
        if (BOOTSTRAP === null) return;

        // Block pending-approval customers with their own message. Their NS customer id is
        // null (no approved CompanyLocation), so the logged-out branch below would catch them
        // anyway — but telling a logged-in customer to "log in" is confusing. This also covers
        // keyboard-Enter submits that bypass the pointer-events:none CSS on the dimmed button.
        if (BOOTSTRAP.isPending === true) {
          event.preventDefault();
          showBanner(
            form,
            message('pendingAddToCartBlocked', 'Your B2B account is pending approval.')
          );
          logDebug('checkout blocked: customer pending approval');
          return;
        }

        // Block logged-out customers outright — they would otherwise pay the Shopify default
        // safety buffer price because the discount function has no payload to enforce.
        if (currentCustomerId() == null) {
          event.preventDefault();
          showBanner(
            form,
            message(
              'checkoutLoggedOut',
              'Please log in to your B2B account to complete your order at negotiated prices.'
            )
          );
          logDebug('checkout blocked: logged-out customer');
          return;
        }

        // For approved B2B customers (draft-submit button present), never allow the form
        // to submit natively — the form action is /cart which would go to Shopify checkout
        // and bypass the draft-order flow (native checkout produces zero discounts for
        // drafts). Always preventDefault and delegate to b2b-draft-order.js via .click().
        // This covers keyboard-Enter submits from the cart note, discount code, or any
        // other form input — paths that bypass the button's own click handler.
        if (draftSubmitButton !== null) {
          event.preventDefault();
          setButtonBusy(button, true);
          hideBanner();
          try {
            logDebug('draft deferred — making sure the signature covers the cart as it is now');
            await ensureFreshSignature();
            setButtonBusy(button, false);
            // Trigger the draft-order module's click handler (bubble phase).
            draftSubmitButton.click();
          } catch (error) {
            logError('draft blocked: cart could not be signed', { error: String(error) });
            setButtonBusy(button, false);
            showBanner(
              form,
              (error && error.buyerMessage) ||
                message(
                  'checkoutSignFailed',
                  'We could not verify your B2B prices. Please refresh the page and try again.'
                )
            );
          }
          return;
        }

        // A submit that does not open checkout (the form's own update path) only posts the quantities
        // and reloads the cart page, which signs itself on load. A browser without event.submitter is
        // treated as a checkout.
        if (event.submitter !== undefined && event.submitter?.name !== 'checkout') return;

        // A team member's view of the store as one customer: the signed cart names that customer, so a checkout
        // would book their order. The cart renders no checkout button; this catches any other submit path.
        if (BOOTSTRAP.staffView === true) {
          event.preventDefault();
          showBanner(form, message('checkoutStaffView', 'This is a staff view: you cannot place the order.'));
          logDebug('checkout blocked: staff view');
          return;
        }

        // Online checkout paused for every market: the cart renders no checkout button, so this only catches a page
        // rendered before the switch.
        if (BOOTSTRAP.checkoutEnabled === false) {
          event.preventDefault();
          showBanner(form, message('checkoutPaused', 'Online orders are paused. Your sales representative will take your order.'));
          logDebug('checkout blocked: online checkout is paused');
          return;
        }

        // Approved B2B checkout never goes out natively. The native post applies updates[] by position,
        // and a signature judged against this tab's memory can be stale in ways this tab never saw
        // (Horizon's debounced quantity, another tab, a page restored from the back/forward cache). So
        // every checkout brings the cart to the quantities on screen, checks the signature against the
        // cart as the server has it, and only then opens checkout.
        event.preventDefault();
        if (checkoutInProgress) return;
        if (!bootstrapIsUsable()) {
          // Approved, but the theme cannot reach the pricing API (no API URL or no market): a checkout
          // now would charge every line the ceiling.
          logError('checkout blocked: B2B pricing is not configured on this theme');
          showBanner(
            form,
            message(
              'checkoutSignFailed',
              'We could not verify your B2B prices. Please refresh the page and try again.'
            )
          );
          return;
        }
        checkoutInProgress = true;
        setButtonBusy(button, true);
        hideBanner();

        try {
          logDebug('checkout — making sure the signature covers the cart as it is now');
          await signCheckoutCart(form);
          logDebug('signature covers the cart — continuing to checkout');
          // Straight to checkout rather than re-submitting the form: its quantities already went out by
          // line key. The button stays busy while the page leaves; pageshow resets it on a back button.
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          window.location.assign(`${root}checkout`);
        } catch (error) {
          logError('checkout blocked: cart could not be signed', { error: String(error) });
          checkoutInProgress = false;
          setButtonBusy(button, false);
          showBanner(
            form,
            (error && error.buyerMessage) ||
              message(
                'checkoutSignFailed',
                'We could not verify your B2B prices. Please refresh the page and try again.'
              ),
            error && error.buyerLinks
          );
        }
      },
      { capture: true }
    );
  }

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  onReady(() => {
    injectSkeletonStyles();
    installCheckoutGuard();
    // Kick off an initial sign on page load so the cart has a fresh signature ready for checkout.
    // Assigns to inflightSignPromise synchronously so the guard can never observe a fresh-page
    // race where the user clicks Checkout before any sign has started.
    scheduleRefresh();
  });

  window.LudipekB2BCartSnapshot = {
    refresh: scheduleRefresh,
    getInflightPromise: () => inflightSignPromise,
    getLastError: () => lastSignError,
    // Shared DOM helpers so the draft-order and pending-gate modules don't duplicate markup.
    showBanner,
    hideBanner,
    setButtonBusy,
    message,
  };
})();
